import { widenableGateKeys } from '../model/widen-gate';
import { SHAPE_TEMPLATES, movementConstraintOf, templateOf } from '../model/shape';
import { familiesUnlockingAt } from '../generator/level-recipe';
import { powerupsUnlockingAt, unlockPuzzleFor } from '../powerups/powerup-registry';
import { targetScoreForLevel } from '../generator/difficulty-curve';
/** The mechanic's id + key, so callers never spell it themselves. */
export const ARROWS_UNLOCK_ID = 'arrows';
const keyOf = (kind, id) => kind === 'arrows' ? 'arrows' : `${kind}:${id}`;
/**
 * Everything unlocking exactly at this level, shapes first (a level can unlock a
 * family and the mechanic at once; the shape is the more concrete of the two, so
 * it is introduced first).
 */
export function unlocksAtLevel(config, levelNumber) {
    // Nothing "unlocks" on level 1: a starter family (no entry in `unlocks.shapes`,
    // so its configured level defaults to 1) is not new — everything is. Announcing
    // it would open the game with a popup saying the block is a block; the existing
    // How-to-Play covers the basics. An unlock event marks a CHANGE in what the
    // player has, which is why this filter belongs here and not in
    // `familiesUnlockingAt` (the generator still guarantees the starter on level 1).
    const band = targetScoreForLevel(config, levelNumber).difficulty;
    const events = levelNumber <= 1
        ? []
        : familiesUnlockingAt(config, levelNumber, band).map((id) => ({
            kind: 'shape',
            id,
            level: levelNumber,
            key: keyOf('shape', id),
        }));
    if (levelNumber === config.unlocks.arrowsUnlockLevel) {
        events.push({
            kind: 'arrows',
            id: ARROWS_UNLOCK_ID,
            level: levelNumber,
            key: keyOf('arrows', ARROWS_UNLOCK_ID),
        });
    }
    // Power-ups last (WP-35.22): a level that unlocks a shape AND a power-up should
    // teach the board piece before the tool that acts on it. `powerupsUnlockingAt`
    // is the same config question the registry answers, so the popup and the bar's
    // locked/unlocked state can never disagree about when it arrives.
    for (const id of powerupsUnlockingAt(config, levelNumber)) {
        events.push({ kind: 'powerup', id, level: levelNumber, key: keyOf('powerup', id) });
    }
    return events;
}
/**
 * Module 65 (WP-65.C1) — the unlocks a player has ENCOUNTERED by this level.
 *
 * ## Why the level-keyed version had to go
 *
 * `unlocksAtLevel` answers "what fires exactly here", which was right for a
 * single ladder every player walked step by step. Tracks break that assumption
 * completely: an Expert-first player's first board is level 61, so they never
 * cross levels 5, 8, 16, 20, 22, 26 or 41 — and under the old rule they would be
 * handed the L-shape, all seven colours, powered gates and four power-ups with
 * **no lesson for any of them, ever**. Not a missed popup: a permanently
 * unreachable one.
 *
 * ## What "encountered" means, and why it is not simply cumulative
 *
 * The naive fix is every unlock at or below this level. That hands an Expert
 * player ten popups on their first board, which teaches nothing and reads as a
 * broken game.
 *
 * So a shape or the arrows mechanic is announced when the player can actually
 * SEE it — it is on the board in front of them — which is the honest moment to
 * explain something, and it paces itself, because a board carries a handful of
 * families rather than all six. Power-ups have no board presence, so they stay
 * config-driven and cumulative; the caller shows one at a time.
 *
 * `board` omitted falls back to the level-keyed behaviour, which is what the
 * Studio and the older tests want.
 */
export function pendingUnlocks(config, levelNumber, seen = [], board, puzzle = levelNumber, track) {
    const already = new Set(seen);
    const events = board
        ? encounteredUnlocks(config, levelNumber, board, puzzle, track)
        : unlocksAtLevel(config, levelNumber);
    return events.filter((event) => !already.has(event.key));
}
/**
 * Everything this board puts in front of the player, plus every power-up they
 * now hold. Order matters and matches `unlocksAtLevel`: shapes, then arrows,
 * then power-ups — the board piece before the tool that acts on it.
 */
export function encounteredUnlocks(config, levelNumber, board, puzzle = levelNumber, track) {
    const events = [];
    // Module 68 (WP-68.1) — nothing on a track's FIRST board is new.
    //
    // `unlocksAtLevel` already makes this argument for level 1 ("announcing it
    // would open the game with a popup saying the block is a block"), and the
    // argument was never about the number 1: it is about the player's first
    // board. Intermediate's is level 26 and Expert's is level 61, so the filter
    // never reached them, and entering either opened the game with `NEW! New
    // shape: square` before a block had been moved (reported 2026-09-22,
    // `Bugs/Intermediate_1.jpeg`, `Bugs/Expert_1..3.jpeg`).
    //
    // The families here are BOARD-derived, so this is not a suppression the
    // caller has to remember: `boardBaselineUnlocks` returns the same set for the
    // caller to mark seen, and content arriving LATER in the track — an L-shape
    // first met at Intermediate puzzle 15 — still gets its popup, which is the
    // whole point of the first-encounter rule.
    if (puzzle > 1)
        events.push(...boardUnlocks(config, levelNumber, board));
    // Power-ups are not on the board, so they stay a config question — cumulative,
    // because a track that starts above an unlock still owes its lesson. Since
    // WP-65.F1 the question is asked of the PUZZLE ordinal, and a power-up whose
    // target is a board feature (Surge → a plate) also waits for a board that
    // carries one: a popup explaining a switch the player has never seen teaches
    // a word, not a mechanic.
    // Module 77 (D4) — asked of `unlockPuzzleFor`. A track that unlocks every
    // power-up at once (Expert, `'all'`) announces none of them (user,
    // 2026-09-28): they are part of that track's baseline, like its first
    // board's shapes, and one "NEW!" card per puzzle blocked play for players
    // Expert is meant to trust. The same rule withholds their lessons
    // (`powerupTriggers`). A power-up met later on another track is still new
    // there, since nothing here marks it seen.
    const allAtOnce = config.tracks.find((t) => t.id === track)?.powerupUnlocks === 'all';
    // Module 92 — a retired power-up is never announced.
    for (const p of allAtOnce ? [] : config.powerups.filter((x) => x.retired !== true)) {
        const at = unlockPuzzleFor(config, track, p.id);
        if (at !== null && at <= puzzle && powerupOnBoard(p.id, board)) {
            events.push({ kind: 'powerup', id: p.id, level: levelNumber, key: keyOf('powerup', p.id) });
        }
    }
    return events;
}
/**
 * The shape families and the arrows mechanic THIS BOARD puts in front of the
 * player, ignoring whether it is the track's first board.
 *
 * Split out at WP-68.1 so the puzzle-1 rule has exactly one definition and two
 * readers: `encounteredUnlocks` skips these events on a track's first board,
 * and the plugin marks the very same set seen (`boardBaselineUnlocks`), so
 * "suppressed" and "absorbed" can never drift apart into a family that is
 * hidden on puzzle 1 and then announced on puzzle 4.
 *
 * Power-ups are deliberately not here: they are not on the board, they are
 * cumulative, and a track entered above their unlock still owes their lesson.
 */
function boardUnlocks(config, levelNumber, board) {
    const events = [];
    // A starter family is still not "new" — on the first board everything is, and
    // announcing it would open the game with a popup saying a block is a block.
    const starters = new Set(config.shapes.families.filter((f) => (config.unlocks.shapes[f.id] ?? 1) <= 1).map((f) => f.id));
    const present = new Set(board.blocks.map((b) => shapeFamilyOf(b)));
    for (const family of config.shapes.families) {
        if (!present.has(family.id) || starters.has(family.id))
            continue;
        events.push({
            kind: 'shape',
            id: family.id,
            level: levelNumber,
            key: keyOf('shape', family.id),
        });
    }
    if (board.blocks.some((b) => movementConstraintOf(b) !== 'free')) {
        events.push({
            kind: 'arrows',
            id: ARROWS_UNLOCK_ID,
            level: levelNumber,
            key: keyOf('arrows', ARROWS_UNLOCK_ID),
        });
    }
    return events;
}
/**
 * Module 68 (WP-68.1) — what a track's FIRST board absorbs as its baseline.
 *
 * Empty for any other puzzle, so the caller needs no `if` of its own: it hands
 * this to `markUnlocksSeen` on every board, and only puzzle 1 has anything in
 * it. Returning the events (not just their keys) keeps it the same currency
 * `markUnlocksSeen` already takes.
 */
export function boardBaselineUnlocks(config, levelNumber, board, puzzle) {
    return puzzle <= 1 ? boardUnlocks(config, levelNumber, board) : [];
}
/**
 * The shape family a block belongs to, by its cell footprint.
 *
 * Derived rather than stored: a `Block` carries `cells`, not the family id the
 * recipe drew it from, and adding one would mean a second answer to a question
 * the geometry already settles.
 */
function shapeFamilyOf(block) {
    // `templateOf` is the one place that knows a legacy block may carry only
    // `size` + `orientation` instead of `cells`.
    const cells = templateOf(block);
    const rows = new Set(cells.map((o) => o.r)).size;
    const cols = new Set(cells.map((o) => o.c)).size;
    // A concave footprint fills fewer cells than its bounding box — that is the L.
    if (cells.length !== rows * cols)
        return 'L';
    const [a, b] = [Math.min(rows, cols), Math.max(rows, cols)];
    return `${a}x${b}`;
}
/**
 * Module 65 (WP-65.F3) — whether this board carries the thing a power-up acts
 * on. Only Surge has a board prerequisite: it powers a plate, and Beginner
 * reaches its first plate at puzzle 22 while the button itself unlocks at 20.
 * The button unlocks on the number the pill promised; the LESSON (unlock
 * popup + coached hand) waits for the first plate, following the first-
 * encounter rule the shapes already follow. Everything else is always on.
 */
export function powerupOnBoard(id, board) {
    if (id === 'surge')
        return (board.puzzle.plates?.length ?? 0) > 0;
    // Module 67 (WP-67.B7) — the Gate Widener acts on a gate that CAN widen; a
    // lesson pointing at a board whose every gate is boxed in or full-wall would
    // demonstrate a refusal. Almost every generated board qualifies, so in
    // practice this fires on the unlock puzzle like the others.
    if (id === 'gateWidener')
        return widenableGateKeys(board).length > 0;
    return true;
}
/** The seen set with these events added — order-stable, no duplicates. */
export function markUnlocksSeen(seen, events) {
    const out = [...seen];
    for (const event of events)
        if (!out.includes(event.key))
            out.push(event.key);
    return out;
}
/** Cell-set key, so a rotated block still matches its family template. */
const shapeKey = (cells) => cells
    .map((c) => `${c.r},${c.c}`)
    .sort()
    .join('|');
/** Every rotation of a family template, as comparable keys. */
function rotationKeys(template) {
    const keys = new Set();
    let cells = template.map((c) => ({ ...c }));
    for (let turn = 0; turn < 4; turn++) {
        const minR = Math.min(...cells.map((c) => c.r));
        const minC = Math.min(...cells.map((c) => c.c));
        keys.add(shapeKey(cells.map((c) => ({ r: c.r - minR, c: c.c - minC }))));
        cells = cells.map((c) => ({ r: c.c, c: -c.r }));
    }
    return keys;
}
/**
 * The block the guided highlight should point at: for a shape unlock, one
 * carrying that family (in any rotation); for Arrows, the first axis-locked
 * block. Null when the board does not contain one — the popup still shows, the
 * practice half is simply skipped rather than pointing at nothing.
 */
export function guidedTargetFor(event, state, config) {
    // A power-up is not carried by any block — its practice half points at the BAR
    // BUTTON instead, which the HUD owns. Returning null here is not "no guidance",
    // it is "not a board target"; see `guidedPowerupFor`.
    if (event.kind === 'powerup')
        return null;
    if (event.kind === 'arrows') {
        return state.blocks.find((b) => movementConstraintOf(b) !== 'free')?.id ?? null;
    }
    const family = config.shapes.families.find((f) => f.id === event.id);
    if (!family)
        return null;
    const template = SHAPE_TEMPLATES[event.id];
    if (!template)
        return null;
    const wanted = rotationKeys(template);
    const match = (block) => wanted.has(shapeKey(templateOf(block)));
    return state.blocks.find(match)?.id ?? null;
}
/**
 * The power-up button the guided highlight should point at, or null when this
 * unlock is not a power-up (WP-35.22). Split from `guidedTargetFor` because the
 * two point at different surfaces — a block on the canvas versus a button in the
 * HUD — and collapsing them into one nullable string would lose which one.
 */
export function guidedPowerupFor(event) {
    return event.kind === 'powerup' ? event.id : null;
}
